import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { browserHistory } from 'react-router';
import MediaQuery from 'react-responsive';
import capitalize from 'capitalize';

import { EditDocumentTemplateDialog } from './EditDocumentTemplateDialog';
import { DialogForConfirmation } from 'components/shared/DialogForConfirmation';
import ArchivedDocumentTemplateDetails from './ArchivedDocumentTemplateDetails';
import { ArchivedActionPane } from './ArchivedActionPane';
import {
  DOCUMENT_TEMPLATE_CONFIGURE_SIGNATURES_PAGE,
  DOCUMENT_TEMPLATE_SETUP_PAGE,
} from './EditDocumentTemplateDialog';

import { ButtonPrimary, useDialog, P } from '@yuhu-inc/yuhui';
import { MdKeyboardArrowDown as DownArrow } from 'react-icons/md';
import { MdKeyboardArrowUp as UpArrow } from 'react-icons/md';

import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { media } from 'helpers/MediaHelper';
import { toFriendlyDateString } from 'helpers/Formatter';
import { usePrevious } from 'helpers/utilities';

const StyledContainer = styled.div`
  width: 100%;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: 100px;
`;

const StyledBreadcrumb = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  padding: 0px 12px 8px 12px;
  ${media.mobile`
    flex-direction: column;
    align-items: center;
  `};
`;

const DesktopHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${media.mobile`
    flex-direction: row;
    align-items: center;
    margin-top: 10px;
  `};
`;

const AdditionalHeaderInformation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 20px;
  text-align: center;
`;

const HeaderDetailsDiv = styled.div`
  padding: 0 32px;
  min-width: 100px;
  border-right: 2px solid #dfdfdf;
  &:last-child {
    border-right: none;
    padding-right: 0;
  }
`;

const StyledBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  align-items: center;
  width: 100%;
`;

const StyledHeaderDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 5px 10px;
`;

const StyledSplitPane = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  justify-content: space-between;
  ${media.mobile`
    flex-direction: column;
  `};
`;

const StyledBorder = styled.div`
  background-color: #ffffff;
  border-radius: 10px;
  padding: 18px 24px;
  height: 100%;
  width: 100%;
  ${media.mobile`
    padding: 18px 18px;
  `};
`;

const StyledActionButton = styled(ButtonPrimary)`
  background-color: #dfe8ff;
  margin-top: 25px;
  margin-left: 10px;
  width: max-content;
  ${media.mobile`
    margin-top: 15px;
    margin-bottom: 8px;
  `};
`;

const StyledDocumentTemplateStatus = styled.p`
  font-style: italic;
  color: red;
  font-size: 18px;
  margin: 0px 0px 0px 10px;
  ${media.mobile`
    font-size: 16px;
  `};
`;

const StyledHeaderTitle = styled.h3`
  font-size: 20px;
  margin: 0;
  white-space: nowrap;
  ${media.mobile`
    font-size: 16px;
  `};
`;

const StyledHr = styled.hr`
  margin-top: 0px;
`;

const StyledHeaderTitleDiv = styled.div`
  display: flex;
  align-items: center;
  margin: 0px 10px;
  ${media.mobile`
    flex-direction: rows;
  `};
`;

const StyledLink = styled.h4`
  font-size: 18px;
  color: #2000ff;
  cursor: pointer;
  margin-right: 5px;
`;

const StyledLinkText = styled.h4`
  font-size: 18px;
`;

const AdditionalInfo = styled.div`
  color: #65666d;
`;

const BoldText = styled.div`
  font-weight: 900;
  font-family: 'Circular-Bold';
  ${media.mobile`
    font-size: 16px;
  `};
`;

const StyledConfirmationHeader = styled(P)`
  font-size: 24px;
  font-family: 'Circular-Black', sans-serif;
  text-align: center;
`;

const StyledConfirmationP = styled(P)`
  font-size: 18px;
  text-align: center;
`;

const StyledConfirmationContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

export const DocumentTemplateDetailsForm = ({
  documentTemplate,
  uploadedFiles,
  archiveDocumentTemplate,
  duplicateDocumentTemplateData,
  duplicateDocumentTemplateSuccess,
  duplicateDocumentTemplate,
  deleteDuplicateDocumentTemplate,
  publishDocumentTemplate,
  refreshEditUrlForDocumentTemplate,
}) => {
  const lang = getCurrentLocale();
  const [initialPageIndex, setInitialPageIndex] = useState(0);

  const handleClose = () => {
    browserHistory.push('/companies/document_templates');
  };

  const archiveConfirmationDialog = useDialog();

  const handleArchive = () => {
    archiveConfirmationDialog.show();
  };

  const handleArchiveConfirmation = (confirmed) => {
    if (confirmed) {
      archiveConfirmationDialog.hide();
      archiveDocumentTemplate(documentTemplate);
    } else {
      archiveConfirmationDialog.hide();
    }
  };

  const approveConfirmationDialog = useDialog();

  const editConfirmationDialog = useDialog();

  const duplciateConfirmationDialog = useDialog();

  const handleApprove = () => {
    approveConfirmationDialog.show();
  };

  const previousStateOfDocTemplateSuccess = usePrevious(duplicateDocumentTemplateSuccess);

  useEffect(() => {
    if (previousStateOfDocTemplateSuccess === undefined) return;

    if (
      previousStateOfDocTemplateSuccess !== duplicateDocumentTemplateSuccess &&
      duplicateDocumentTemplateSuccess === true
    ) {
      duplciateConfirmationDialog.show();
    }
  }, [
    duplicateDocumentTemplateSuccess,
    duplciateConfirmationDialog,
    previousStateOfDocTemplateSuccess,
  ]);

  const handleApproveConfirmation = (confirmed) => {
    if (confirmed) {
      approveConfirmationDialog.hide();
      publishDocumentTemplate(documentTemplate);
    } else {
      approveConfirmationDialog.hide();
    }
  };

  const handleChangeMapping = async (callback) => {
    // The edit_url stored on the document template has an exipry of 1hr
    await refreshEditUrlForDocumentTemplate(documentTemplate.id);
    // Forcibly set the first page of the edit wizard to the last signature page
    setInitialPageIndex(DOCUMENT_TEMPLATE_CONFIGURE_SIGNATURES_PAGE);
    // callback to toggle the edit dialog
    if (callback) {
      callback();
    }
  };

  const isArchived = documentTemplate.archived;
  const awaitingApproval = documentTemplate.status === 'ready_for_review';

  const mappingButtonText =
    documentTemplate.is_mapped === true
      ? 'landlordPortal.documentTemplate.changeMapping'
      : 'landlordPortal.documentTemplate.createMapping';

  const archiveButtonText = isArchived
    ? 'landlordPortal.documentTemplate.unArchiveDocumentTemplate'
    : 'landlordPortal.documentTemplate.archiveDocumentTemplate';

  const archiveDialogText = isArchived
    ? 'landlordPortal.documentTemplateDetailsForm.unArchiveDialogText'
    : 'landlordPortal.documentTemplateDetailsForm.archiveDialogText';

  const editDialogText = 'landlordPortal.documentTemplateDetailsForm.editDocumentDialogText';

  return (
    <StyledContainer>
      <StyledBreadcrumb>
        <div style={{ display: 'flex' }}>
          <StyledLink onClick={handleClose}>
            {getTranslatedString('landlordPortal.documentTemplate.documentTemplate')}
          </StyledLink>
          <StyledLinkText>{` - ${documentTemplate.title}`}</StyledLinkText>
        </div>

        <div>
          <StyledActionButton
            contentKey="landlordPortal.documentTemplate.duplicate"
            onClick={() => {
              duplicateDocumentTemplate(documentTemplate);
            }}
          />

          <StyledActionButton
            contentKey="landlordPortal.documentTemplate.edit"
            onClick={() => {
              editConfirmationDialog.show();
            }}
          />

          <EditDocumentTemplateDialog initialPageIndex={initialPageIndex}>
            {({ toggle }) => (
              <React.Fragment>
                <StyledActionButton
                  type="button"
                  contentKey={mappingButtonText}
                  onClick={() => handleChangeMapping(toggle)}
                />
              </React.Fragment>
            )}
          </EditDocumentTemplateDialog>

          {awaitingApproval && (
            <StyledActionButton
              type="button"
              contentKey="landlordPortal.documentTemplate.publish"
              onClick={handleApprove}
            />
          )}

          <StyledActionButton
            type="button"
            contentKey={archiveButtonText}
            onClick={handleArchive}
          />
        </div>
      </StyledBreadcrumb>
      <StyledBodyContainer>
        <DocumentTemplateHeader
          documentTemplate={documentTemplate}
          lang={lang}
          isArchived={isArchived}
        />
        <StyledSplitPane>
          <ArchivedDocumentTemplateDetails
            documentTemplate={documentTemplate}
            uploadedFiles={uploadedFiles}
          />
          {isArchived && (
            <ArchivedActionPane
              handleArchive={handleArchive}
              archiveButtonText={archiveButtonText}
            />
          )}
        </StyledSplitPane>
      </StyledBodyContainer>

      <EditDocumentTemplateDialog initialPageIndex={initialPageIndex}>
        {({ toggle }) => (
          <React.Fragment>
            <DialogForConfirmation
              handleConfirmation={(confirmed) => {
                if (confirmed) {
                  editConfirmationDialog.hide();
                  setInitialPageIndex(DOCUMENT_TEMPLATE_SETUP_PAGE);
                  toggle();
                } else {
                  editConfirmationDialog.hide();
                }
              }}
              noKey="landlordPortal.documentTemplate.carousel.cancel"
              yesKey="landlordPortal.documentTemplate.carousel.editDocument"
              {...editConfirmationDialog}
            >
              <StyledConfirmationContainer>
                <StyledConfirmationHeader contentKey="landlordPortal.documentTemplateDetailsForm.editDocumentHeader" />
                <StyledConfirmationP contentKey={editDialogText} />
              </StyledConfirmationContainer>
            </DialogForConfirmation>
          </React.Fragment>
        )}
      </EditDocumentTemplateDialog>

      <DialogForConfirmation
        handleConfirmation={(confirmed) => {
          if (confirmed) {
            deleteDuplicateDocumentTemplate(duplicateDocumentTemplateData);
            duplciateConfirmationDialog.hide();
          } else {
            duplciateConfirmationDialog.hide();
          }
        }}
        noKey="landlordPortal.documentTemplate.carousel.okay"
        yesKey="landlordPortal.documentTemplateDetailsForm.undoDuplicateButtonText"
        {...duplciateConfirmationDialog}
      >
        <StyledConfirmationContainer>
          <StyledConfirmationHeader contentKey="landlordPortal.documentTemplateDetailsForm.duplicateDialogHeader" />
          <StyledConfirmationP contentKey="landlordPortal.documentTemplateDetailsForm.duplicateTemplateConfirmationText">
            {' '}
            {duplicateDocumentTemplateData && duplicateDocumentTemplateData.attributes.title}{' '}
          </StyledConfirmationP>
        </StyledConfirmationContainer>
      </DialogForConfirmation>

      <DialogForConfirmation
        handleConfirmation={handleArchiveConfirmation}
        noKey="landlordPortal.documentTemplate.carousel.cancel"
        yesKey={archiveButtonText}
        {...archiveConfirmationDialog}
      >
        <StyledConfirmationContainer>
          <StyledConfirmationHeader contentKey="landlordPortal.documentTemplateDetailsForm.archiveDialogHeader" />
          <StyledConfirmationP contentKey={archiveDialogText} />
        </StyledConfirmationContainer>
      </DialogForConfirmation>

      <DialogForConfirmation
        handleConfirmation={handleApproveConfirmation}
        noKey="landlordPortal.documentTemplate.carousel.cancel"
        yesKey="landlordPortal.documentTemplate.approve"
        {...approveConfirmationDialog}
      >
        <StyledConfirmationContainer>
          <StyledConfirmationHeader contentKey="landlordPortal.documentTemplateDetailsForm.approveDialogHeader" />
          <StyledConfirmationP contentKey="landlordPortal.documentTemplateDetailsForm.approveDialogText" />
        </StyledConfirmationContainer>
      </DialogForConfirmation>
    </StyledContainer>
  );
};

const StyledMobileHeaderContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const StyledMobileAdditionalInfoTitle = styled.p`
  margin: 0px;
  font-size: 16px;
  margin-bottom: 3px;
`;

const StyledMobileAdditionalInfoContainer = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const StyledArrowContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: -2px;
  margin-bottom: -10px;
`;

const DocumentTemplateHeader = ({ documentTemplate, lang, isArchived }) => {
  const totalNumberApplicants = (documentTemplate) =>
    documentTemplate.max_num_applicants + documentTemplate.max_num_guarantors;

  const [showMore, setShowMore] = useState(false);

  const handleShowMore = () => {
    setShowMore(!showMore);
  };

  return (
    <StyledHeaderDetails>
      <StyledBorder>
        <MediaQuery query="(max-device-width: 750px)">
          <StyledMobileHeaderContainer>
            <StyledHeaderTitle>
              {`${capitalize(documentTemplate.title)} - ${totalNumberApplicants(
                documentTemplate
              )}${' '}${getTranslatedString(
                'landlordPortal.documentTemplateDetailsForm.applicants'
              )}`}
            </StyledHeaderTitle>
            <StyledDocumentTemplateStatus>
              {isArchived
                ? getTranslatedString('landlordPortal.documentTemplateDetailsForm.archived')
                : getTranslatedString(
                    `landlordPortal.documentTemplateDetailsForm.${documentTemplate.status}`
                  )}
            </StyledDocumentTemplateStatus>
          </StyledMobileHeaderContainer>
          <div>
            {showMore && (
              <React.Fragment>
                <StyledHr />
                <StyledMobileAdditionalInfoContainer>
                  <StyledMobileAdditionalInfoTitle>
                    {getTranslatedString(
                      'landlordPortal.documentTemplateDetailsForm.activeProperty'
                    )}
                  </StyledMobileAdditionalInfoTitle>
                  <BoldText>{documentTemplate.property_uuids.length}</BoldText>
                </StyledMobileAdditionalInfoContainer>
                <StyledMobileAdditionalInfoContainer>
                  <StyledMobileAdditionalInfoTitle>
                    {getTranslatedString('landlordPortal.documentTemplateDetailsForm.createdOn')}
                  </StyledMobileAdditionalInfoTitle>
                  <BoldText>
                    {toFriendlyDateString(documentTemplate.created_at, {
                      language: lang,
                    })}
                  </BoldText>
                </StyledMobileAdditionalInfoContainer>
                <StyledMobileAdditionalInfoContainer>
                  <StyledMobileAdditionalInfoTitle>
                    {getTranslatedString('landlordPortal.documentTemplateDetailsForm.createdBy')}
                  </StyledMobileAdditionalInfoTitle>
                  <BoldText>{documentTemplate.creator_name}</BoldText>
                </StyledMobileAdditionalInfoContainer>
              </React.Fragment>
            )}
            <StyledArrowContainer>
              {showMore ? (
                <UpArrow size={24} onClick={handleShowMore} style={{ color: '#001AFF' }} />
              ) : (
                <DownArrow size={24} onClick={handleShowMore} style={{ color: '#001AFF' }} />
              )}
            </StyledArrowContainer>
          </div>
        </MediaQuery>
        <MediaQuery query="(min-device-width: 751px)">
          <DesktopHeader>
            <StyledHeaderTitleDiv>
              <StyledHeaderTitle>
                {`${capitalize(documentTemplate.title)} - ${totalNumberApplicants(
                  documentTemplate
                )}${' '}${getTranslatedString(
                  'landlordPortal.documentTemplateDetailsForm.applicants'
                )}`}
              </StyledHeaderTitle>
              <StyledDocumentTemplateStatus>
                {isArchived
                  ? getTranslatedString('landlordPortal.documentTemplateDetailsForm.archived')
                  : getTranslatedString(
                      `landlordPortal.documentTemplateDetailsForm.${documentTemplate.status}`
                    )}
              </StyledDocumentTemplateStatus>
            </StyledHeaderTitleDiv>
            <AdditionalHeaderInformation>
              <HeaderDetailsDiv>
                <AdditionalInfo>
                  {getTranslatedString('landlordPortal.documentTemplateDetailsForm.activeProperty')}
                </AdditionalInfo>
                <BoldText>{documentTemplate.property_uuids.length}</BoldText>
              </HeaderDetailsDiv>
              <HeaderDetailsDiv>
                <AdditionalInfo>
                  {getTranslatedString('landlordPortal.documentTemplateDetailsForm.createdOn')}
                </AdditionalInfo>
                <BoldText>
                  {toFriendlyDateString(documentTemplate.created_at, {
                    language: lang,
                  })}
                </BoldText>
              </HeaderDetailsDiv>
              <HeaderDetailsDiv>
                <AdditionalInfo>
                  {getTranslatedString('landlordPortal.documentTemplateDetailsForm.createdBy')}
                </AdditionalInfo>
                <BoldText>{documentTemplate.creator_name}</BoldText>
              </HeaderDetailsDiv>
            </AdditionalHeaderInformation>
          </DesktopHeader>
        </MediaQuery>
      </StyledBorder>
    </StyledHeaderDetails>
  );
};
